import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';
import { V1_PRIORITY_OPTIONS, needsUrgentConfirm, urgentConfirmMessage } from '../lib/priorities';
import { useAuth } from '../context/AuthContext';
import { canCreateTicket } from '../lib/features';
import { useRefs } from '../context/RefsContext';
import { isDtStaff } from '../lib/staff';
import { creationNotifiers, notifierChoices } from '../lib/ticketEdit';

const TYPES = ['Bug', 'Evolution', 'Question', 'DP'] as const;
type Type = (typeof TYPES)[number];

interface CreateResponse {
  display_id: number;
  id_support: number[];
}

interface Eligibility {
  allowed: boolean;
  code?: string;
  error?: string;
}

interface ClientUser {
  id_user: number;
  nom_user: string;
}

export function NewTicketPage() {
  const { user } = useAuth();
  const { clients, traitants } = useRefs();
  const navigate = useNavigate();

  // Staff = agent Digital Team (l'API refuse le reste : autre client, auteur, traitants).
  const isStaff = isDtStaff(user);

  const [idClient, setIdClient] = useState<number | ''>('');
  const [nomTache, setNomTache] = useState('');
  const [typeTache, setTypeTache] = useState<Type>('Bug');
  const [priorite, setPriorite] = useState(10);
  const [description, setDescription] = useState('');
  const [idSupport, setIdSupport] = useState<number[]>([]);
  const [overrideAssignees, setOverrideAssignees] = useState(false);
  // Auteur du point (liste « Auteur » du V1) : le client d'un point est celui
  // de son auteur. Obligatoire quand un agent crée pour un autre client.
  const [idAuteur, setIdAuteur] = useState<number | ''>('');
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  // Notifiés (agents) : utilisateurs du client de l'auteur, auteur exclu,
  // décochés par défaut ; mêmes choix que la modification du point.
  const [linkNotifier, setLinkNotifier] = useState<number[]>([]);
  // Règles du V1 pour les clients (plafond de points ouverts, week-end).
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-init id_client : si l'utilisateur n'est pas staff DT, on force son id_client.
  useEffect(() => {
    if (!user) return;
    if (!isStaff) {
      setIdClient(user.id_client);
    }
  }, [user, isStaff]);

  useEffect(() => {
    if (!user || !canCreateTicket(user)) return;
    const ctl = new AbortController();
    apiGet<Eligibility>('/api/tickets/new/eligibility', ctl.signal)
      .then(setEligibility)
      .catch(() => {});
    return () => ctl.abort();
  }, [user]);

  // Utilisateurs du client choisi (agents seulement) : liste « Auteur » (autre
  // client que le sien) et cases « Notifiés » (tout client).
  const authorRequired = isStaff && idClient !== '' && !!user && idClient !== user.id_client;
  useEffect(() => {
    setIdAuteur('');
    setClientUsers([]);
    setLinkNotifier([]);
    if (!isStaff || idClient === '' || !user) return;
    const ctl = new AbortController();
    apiGet<{ items: ClientUser[] }>(`/api/refs/clients/${idClient}/users`, ctl.signal)
      .then((r) => setClientUsers(r.items))
      .catch(() => {});
    return () => ctl.abort();
  }, [idClient, isStaff, user]);

  // Traitants par défaut du client choisi (client.link_user) : « Personnaliser »
  // part de ceux-là, pré-cochés (retour 16). `repliV1` : le client n'en a pas,
  // le point ira à l'utilisateur 3 comme dans le V1.
  const [repliV1, setRepliV1] = useState(false);
  const [defaultIds, setDefaultIds] = useState<number[]>([]);
  useEffect(() => {
    setIdSupport([]);
    setDefaultIds([]);
    setRepliV1(false);
    if (!isStaff || idClient === '') return;
    const ctl = new AbortController();
    apiGet<{ ids: number[]; repli_v1: boolean }>(`/api/refs/clients/${idClient}/traitants-defaut`, ctl.signal)
      .then((r) => {
        setIdSupport(r.ids);
        setDefaultIds(r.ids);
        setRepliV1(r.repli_v1);
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [idClient, isStaff]);

  // Auteur effectif : celui choisi, ou l'agent lui-même pour son propre client.
  const authorId: number | null = !isStaff || idClient === '' || !user ? null : authorRequired ? (idAuteur === '' ? null : idAuteur) : user.id_user;
  const notifiers = authorId === null ? [] : notifierChoices(clientUsers, authorId, [], () => undefined);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.nom_client.localeCompare(b.nom_client)),
    [clients],
  );
  const sortedTraitants = useMemo(
    () => [...traitants].sort((a, b) => a.nom_user.localeCompare(b.nom_user)),
    [traitants],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (idClient === '' || idClient === 0) {
      setErr('Sélectionnez un client');
      return;
    }
    if (authorRequired && idAuteur === '') {
      setErr("Choisissez l'auteur du point parmi les utilisateurs du client");
      return;
    }
    if (nomTache.trim().length < 3) {
      setErr('Le titre doit faire au moins 3 caractères');
      return;
    }
    // Priorité urgente (≤ 5) : courriel à tous les traitants, confirmation d'abord.
    if (needsUrgentConfirm(priorite) && !window.confirm(urgentConfirmMessage(priorite, 'creation'))) {
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const res = await apiPost<CreateResponse>('/api/tickets', {
        id_client: idClient,
        nom_tache: nomTache,
        type_tache: typeTache,
        priorite,
        description,
        id_user: authorRequired && idAuteur !== '' ? idAuteur : undefined,
        id_support: overrideAssignees ? idSupport : undefined,
        link_notifier: isStaff ? creationNotifiers(linkNotifier, notifiers) : undefined,
      });
      navigate(`/tickets/${res.display_id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur création');
      setSubmitting(false);
    }
  }

  function toggleNotifier(id: number) {
    setLinkNotifier((curr) => (curr.includes(id) ? curr.filter((n) => n !== id) : [...curr, id]));
  }

  function toggleAssignee(id: number) {
    setIdSupport((curr) => (curr.includes(id) ? curr.filter((n) => n !== id) : [...curr, id]));
  }

  if (!canCreateTicket(user)) {
    return (
      <div style={{ maxWidth: 700 }}>
        <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 16 }}>Nouveau point</h1>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
          Pendant la transition, les nouveaux points se créent encore dans Jarvis V1.{' '}
          <Link to="/tickets?preset=a_traiter">Retour à la liste</Link>
        </div>
      </div>
    );
  }

  if (eligibility && !eligibility.allowed) {
    return (
      <div style={{ maxWidth: 700 }}>
        <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 16 }}>Nouveau point</h1>
        <div
          role="alert"
          style={{ color: 'var(--danger)', fontSize: 'var(--fs-sm)', background: 'var(--danger-soft)', padding: 10, borderRadius: 6, marginBottom: 12 }}
        >
          {eligibility.error}
        </div>
        <Link to="/tickets?preset=a_traiter">Retour à la liste</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/tickets?preset=a_traiter" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
      </div>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 16 }}>Nouveau point</h1>

      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 14 }}>
        {isStaff && (
          <Field label="Client">
            <select
              value={idClient}
              onChange={(e) => setIdClient(Number(e.target.value) || '')}
              required
              style={inputStyle}
            >
              <option value="">— Choisir —</option>
              {sortedClients.map((c) => (
                <option key={c.id_client} value={c.id_client}>
                  {c.nom_client}
                </option>
              ))}
            </select>
          </Field>
        )}

        {authorRequired && (
          <Field label="Auteur (utilisateur du client)">
            <select
              value={idAuteur}
              onChange={(e) => {
                const id = Number(e.target.value) || '';
                setIdAuteur(id);
                // L'auteur n'est pas son propre notifié (liste du V1).
                setLinkNotifier((curr) => curr.filter((n) => n !== id));
              }}
              required
              style={inputStyle}
            >
              <option value="">— Choisir —</option>
              {clientUsers.map((u) => (
                <option key={u.id_user} value={u.id_user}>
                  {u.nom_user}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Titre">
          <input
            value={nomTache}
            onChange={(e) => setNomTache(e.target.value)}
            placeholder="Résumé court du problème"
            required
            minLength={3}
            maxLength={200}
            style={inputStyle}
          />
        </Field>

        {/* Type (valeurs courtes) en colonne etroite, priorite en colonne large :
            dans l'autre sens, les libelles de priorite du V1 (« 4 - Très urgent
            (Pb bloquant en production) ») etaient coupes dans 120 px. */}
        <div className="grid-narrow-main" style={{ gap: 12 }}>
          <Field label="Type">
            <select value={typeTache} onChange={(e) => setTypeTache(e.target.value as Type)} style={inputStyle}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priorité">
            <select
              value={priorite}
              onChange={(e) => setPriorite(Number(e.target.value))}
              style={{ ...inputStyle, width: '100%', minWidth: 0 }}
            >
              {V1_PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={8}
            placeholder="Décrire le contexte, les étapes pour reproduire, le comportement attendu…"
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </Field>

        {isStaff && (
          <Field
            label={
              <span>
                Traitants{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-mute)', fontSize: 'var(--fs-xs)' }}>
                  (sinon : traitants par défaut du client, remplacés s’ils sont en congé)
                </span>
              </span>
            }
          >
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)', marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={overrideAssignees}
                onChange={(e) => setOverrideAssignees(e.target.checked)}
              />
              Personnaliser
            </label>
            {idClient !== '' && (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 8 }}>
                {repliV1
                  ? "Ce client n'a pas de traitant par défaut : le point ira à l'utilisateur 3 (règle du V1)."
                  : `Par défaut : ${
                      sortedTraitants
                        .filter((t) => defaultIds.includes(t.id_user))
                        .map((t) => t.nom_user)
                        .join(', ') || '—'
                    }`}
              </div>
            )}
            {overrideAssignees && (
              <div
                className="grid-2"
                style={{
                  gap: 6,
                  padding: 10,
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: 'var(--bg-soft)',
                }}
              >
                {sortedTraitants.map((t) => (
                  <label key={t.id_user} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
                    <input
                      type="checkbox"
                      checked={idSupport.includes(t.id_user)}
                      onChange={() => toggleAssignee(t.id_user)}
                    />
                    {t.nom_user}
                  </label>
                ))}
              </div>
            )}
          </Field>
        )}

        {isStaff && idClient !== '' && (
          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
            <legend style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-mute)', marginBottom: 4, padding: 0 }}>
              Notifiés{' '}
              <span style={{ fontWeight: 400, fontSize: 'var(--fs-xs)' }}>(reçoivent les messages publics du point)</span>
            </legend>
            {authorId === null ? (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Choisissez d’abord l’auteur.</div>
            ) : (
              <div style={{ display: 'grid', gap: 4, maxHeight: 180, overflow: 'auto' }}>
                {notifiers.length === 0 && (
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Aucun autre utilisateur chez ce client</div>
                )}
                {notifiers.map((n) => (
                  <label key={n.id_user} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
                    <input type="checkbox" checked={linkNotifier.includes(n.id_user)} onChange={() => toggleNotifier(n.id_user)} />
                    {n.nom_user}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-sm)', background: 'var(--danger-soft)', padding: 10, borderRadius: 6 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" disabled={submitting} className="btn primary">
            {submitting ? 'Création…' : 'Créer le point'}
          </button>
          <Link to="/tickets?preset=a_traiter" className="btn">
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-mute)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 'var(--fs-sm)',
  background: 'var(--bg)',
};
